import { useEffect, useState } from 'react';
import { Save, Plus, ExternalLink, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminSEOPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newPath, setNewPath] = useState('');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    const load = async () => {
        try {
            setLoading(true);
            const r = await api.get('/seo');
            setItems(r.data.items || []);
        } catch { toast.error('Failed to load SEO'); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const update = (idx, field, value) => {
        const arr = [...items]; arr[idx] = { ...arr[idx], [field]: value }; setItems(arr);
    };
    const save = async (idx) => {
        const it = items[idx];
        try {
            await api.put('/admin/seo', it);
            toast.success(`Saved ${it.path}`);
        } catch { toast.error('Save failed'); }
    };
    const addNew = () => {
        const path = (newPath || '').trim();
        if (!path.startsWith('/')) { toast.error('Path must start with /'); return; }
        if (items.some((i) => i.path === path)) { toast.error('Path already exists'); return; }
        setItems([...items, { path, title: '', description: '', keywords: '', og_image: '/favicon.png', robots: 'index, follow' }]);
        setNewPath('');
    };

    return (
        <div data-testid="admin-seo-page" className="space-y-6">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">SEO management</h1>
                    <p className="text-sm text-muted-foreground mt-1">Per-route titles, descriptions, OG images, and robots directives. Sitemap auto-generated.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button asChild variant="secondary" size="sm" className="border border-white/10">
                        <a href={`${backendUrl}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" /> View sitemap</a>
                    </Button>
                    <Button asChild variant="secondary" size="sm" className="border border-white/10">
                        <a href={`/robots.txt`} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" /> View robots.txt</a>
                    </Button>
                </div>
            </header>

            <div className="dt-glass rounded-xl p-4 flex flex-col sm:flex-row gap-2 items-start sm:items-end">
                <div className="flex-1 max-w-md w-full">
                    <Label className="text-xs font-mono uppercase tracking-widest text-foreground/70">Add new SEO entry</Label>
                    <Input value={newPath} onChange={(e) => setNewPath(e.target.value)} placeholder="e.g. /about" className="bg-white/5 border-white/10 mt-1.5" />
                </div>
                <Button onClick={addNew} className="bg-primary text-primary-foreground hover:bg-primary/90"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <div className="dt-glass rounded-xl p-2">
                {loading ? <div className="py-10 text-center text-muted-foreground">Loading…</div> : (
                    <Accordion type="single" collapsible defaultValue="item-0">
                        {items.map((it, i) => (
                            <AccordionItem key={it.path} value={`item-${i}`} className="border-white/10">
                                <AccordionTrigger className="hover:no-underline px-3" data-testid={`seo-trigger-${it.path.replaceAll('/', '_') || 'root'}`}>
                                    <div className="flex items-center gap-3 text-left">
                                        <Search className="h-4 w-4 text-primary" />
                                        <span className="font-mono text-sm">{it.path}</span>
                                        <span className="text-xs text-muted-foreground truncate max-w-md">— {it.title}</span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-3 pb-4 pt-1 space-y-3">
                                    <Field label="Title"><Input value={it.title || ''} onChange={(e) => update(i, 'title', e.target.value)} className="bg-white/5 border-white/10" /></Field>
                                    <Field label="Description"><Textarea rows={2} value={it.description || ''} onChange={(e) => update(i, 'description', e.target.value)} className="bg-white/5 border-white/10" /></Field>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <Field label="Keywords"><Input value={it.keywords || ''} onChange={(e) => update(i, 'keywords', e.target.value)} className="bg-white/5 border-white/10" /></Field>
                                        <Field label="OG image URL"><Input value={it.og_image || ''} onChange={(e) => update(i, 'og_image', e.target.value)} className="bg-white/5 border-white/10" /></Field>
                                        <Field label="Robots directive"><Input value={it.robots || ''} onChange={(e) => update(i, 'robots', e.target.value)} className="bg-white/5 border-white/10" placeholder="index, follow" /></Field>
                                    </div>
                                    <div className="flex justify-end">
                                        <Button onClick={() => save(i)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`seo-save-${it.path.replaceAll('/', '_') || 'root'}`}><Save className="h-4 w-4 mr-1" /> Save</Button>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-xs font-mono uppercase tracking-widest text-foreground/70">{label}</Label>{children}</div>;
}
